import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  StatTile,
  Empty,
  Loading,
  ErrorBox,
  HowCalculated,
  DefinitionTable,
  fmtMins,
  fmtDate,
} from '../MetricsUI';
import { Histogram, DotPlot, TargetBar, Donut, CountTile } from './charts';
import Section from '../Section';
import PeopleTable from './PeopleTable';
import ContentCoverage from './ContentCoverage';
import CohortFilters, {
  EMPTY_FILTERS,
  applyFilters,
  scoreFor,
  improvementFor,
  windowBody,
} from './CohortFilters';
import { fetchCohortOverview, fetchCohortActivity } from '../../../apis/metrics.api';
import { SHOW_LEADERBOARDS } from '../metricsFlags';

const ADOPTION_TARGET = 90;
const BAND = 2; // improvement inside +/- this reads flat

/** median of a numeric array, or null. */
const median = (xs) => {
  const s = xs.filter((v) => v != null).sort((a, b) => a - b);
  if (!s.length) return null;
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
};
const mean = (xs) => {
  const s = xs.filter((v) => v != null);
  return s.length ? s.reduce((a, b) => a + b, 0) / s.length : null;
};
const histOf = (values, { lo = 0, hi = 100, width = 10 } = {}) => {
  const count = Math.max(1, Math.ceil((hi - lo) / width));
  const bins = Array.from({ length: count }, (_, i) => ({
    from: lo + i * width,
    to: i === count - 1 ? hi : lo + (i + 1) * width,
    n: 0,
  }));
  values.forEach((v) => {
    if (v == null || v < lo || v > hi) return;
    bins[Math.min(count - 1, Math.floor((v - lo) / width))].n++;
  });
  return { bins };
};
const statOf = (values) => {
  const s = values.filter((v) => v != null).sort((a, b) => a - b);
  if (!s.length) return { n: 0, mean: null, median: null, p25: null, p75: null };
  const q = (p) => {
    const pos = (s.length - 1) * p;
    const lo = Math.floor(pos);
    const hi = Math.ceil(pos);
    return lo === hi ? s[lo] : s[lo] + (s[hi] - s[lo]) * (pos - lo);
  };
  return { n: s.length, mean: mean(s), median: q(0.5), p25: q(0.25), p75: q(0.75) };
};

export default function CohortPage({ idToken, organizationId, organizationIds, viewer, onOpenLearner, onBack, backLabel }) {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [payload, setPayload] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  /* Joined so the effect compares by value — a fresh array each render would
     refetch forever. */
  const orgIdsKey = (organizationIds || []).join(',');
  const [activity, setActivity] = useState(null);
  const [error, setError] = useState(null);

  /*
   * MEMOISED ON THE SELECTION, NOT RECOMPUTED PER RENDER.
   *
   * windowBody reads Date.now(), so calling it inline produced a different
   * from/to on every single render. As an effect dependency that meant: render →
   * new window → effect refires → setPayload(null) → render → new window …, an
   * infinite fetch loop that showed as the page flickering on "Loading
   * organisation" and never settling, while hammering the endpoint.
   *
   * The dependency has to be what the USER chose — the range key and the two
   * custom dates — so "now" is captured once when the selection changes and then
   * held. windowBody reads only these three fields.
   *
   * It still returns null while a custom range is incomplete or backwards, so
   * the page holds off rather than firing a request the server would reject.
   */
  const { range: rangeKey, from: customFrom, to: customTo } = filters;
  /* Joined into a string so the memo compares by value — a fresh array every
     render would make this an infinite fetch loop. */
  const regionKey = (filters.regions || []).slice().sort().join(',');
  const win = useMemo(
    () => {
      const w = windowBody({ range: rangeKey, from: customFrom, to: customTo });
      if (!w) return null;
      return regionKey ? { ...w, regions: regionKey.split(',') } : w;
    },
    [rangeKey, customFrom, customTo, regionKey]
  );

  /*
   * Refetch WITHOUT clearing what is on screen.
   *
   * Nulling the payload first swapped the whole page for a spinner, and when
   * the new data arrived every Section remounted — which reset each one to
   * collapsed. Changing the date range or a region therefore threw away the
   * detail you had open, twice: once on the way out and once on the way back.
   *
   * Keeping the old payload means the sections never unmount, so their open
   * state survives, and `refreshing` drives a quiet indicator instead of a
   * full-page loading state. Only the very first load has nothing to show.
   */
  useEffect(() => {
    if (!win) return undefined;
    let cancelled = false;
    setRefreshing(true);
    setError(null);
    fetchCohortOverview(idToken, { organizationId, organizationIds, ...win })
      .then((d) => {
        if (cancelled) return;
        setPayload(d);
        setRefreshing(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e.message);
        setRefreshing(false);
      });
    return () => {
      cancelled = true;
    };
    // organizationIds is read inside but orgIdsKey is the dependency: the array
    // is a new identity every render, so depending on it refetches forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idToken, organizationId, orgIdsKey, win]);

  /*
   * Activity is fetched once and NOT refetched when the range changes: it is a
   * fixed 12 months by design. Tied to the filter above it, the trend would
   * collapse to the period just selected and stop being a trend.
   */
  useEffect(() => {
    let cancelled = false;
    setActivity(null);
    fetchCohortActivity(idToken, { organizationId, organizationIds })
      .then((d) => !cancelled && setActivity(d))
      .catch(() => !cancelled && setActivity({ weeks: [], days: [] }));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idToken, organizationId, orgIdsKey]);

  const all = useMemo(() => (payload ? payload.members : []), [payload]);
  const rows = useMemo(() => applyFilters(all, filters), [all, filters]);

  if (error) return <ErrorBox message={error} />;

  if (!payload) {
    return (
      <div className="space-y-8">
        {onBack && (
          <button onClick={onBack} className="text-sm text-lavender hover:underline">
            ← {backLabel || 'Back'}
          </button>
        )}
        {/* The filter bar stays up while the range is incomplete or loading —
            hiding it would take away the very control being used. */}
        <CohortFilters
          filters={filters}
          onChange={setFilters}
          members={[]}
          filterOptions={{}}
          anonymous={false}
        />
        {win ? (
          <Loading what="organisation" />
        ) : (
          <p className="text-sm italic text-gray-500">
            Choose a start and end date to load the organisation.
          </p>
        )}
      </div>
    );
  }

  const { organization, timeSplit } = payload;
  const anonymous = !!organization.anonymous;
  const filtered = rows.length !== all.length;

  return (
    <div className="space-y-8">
      {onBack && (
        <button onClick={onBack} className="text-sm text-lavender hover:underline">
          ← {backLabel || 'Back'}
        </button>
      )}

      <OrgHeader organization={organization} people={all.length} viewer={viewer} />

      <CohortFilters
        filters={filters}
        onChange={setFilters}
        members={all}
        filterOptions={payload.filterOptions}
        anonymous={anonymous}
        membersByRegion={payload.membersByRegion || []}
      />

      {/* A quiet indicator rather than a loading state: the figures below are the
          previous ones for a moment, and saying so is better than blanking the
          page and losing every open section. */}
      {refreshing && (
        <p className="-mt-4 flex items-center gap-2 text-xs text-gray-500">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-lavender" />
          Updating for the new selection — the figures below are the previous ones until it arrives.
        </p>
      )}

      {filtered && !refreshing && (
        <p className="-mt-4 text-xs text-gray-500">
          Showing <b className="text-navy">{rows.length}</b> of {all.length} people. Every figure below
          reflects the filter.
        </p>
      )}

      <AdoptionSection
        rows={rows}
        allCount={all.length}
        timeSplit={timeSplit}
        activity={activity}
        window={payload.window}
        seats={organization.seats}
        onOpenLearner={onOpenLearner}
      />

      <ImprovementSection rows={rows} filters={filters} onOpenLearner={onOpenLearner} />

      <ContentCoverageSection content={payload.content} onOpenLearner={onOpenLearner} />

      <PeopleSection
        rows={rows}
        attention={payload.attention}
        anonymous={anonymous}
        orgName={organization.name}
        onOpenLearner={onOpenLearner}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * header
 * ------------------------------------------------------------------ */

function OrgHeader({ organization, people, viewer }) {
  const ROLE = { admin: 'Admin', leader: 'Manager', trainee: 'Trainee' };
  return (
    <div className="rounded-lg bg-navy p-6 text-white shadow">
      <div className="flex flex-wrap items-center justify-between gap-6">
        <div>
          <p className="text-xs uppercase tracking-widest text-indigo-200">Organisation</p>
          <h1 className="text-2xl font-bold leading-tight">{organization.name}</h1>
          <p className="mt-0.5 text-sm text-indigo-200">
            {people} {people === 1 ? 'employee' : 'employees'}
            {organization.seats ? ` on ${organization.seats} seats` : ' · no seat count on record'}
            {` · ${organization.chapterGroup} content`}
            {organization.learningPathEnabled ? ' · levels on' : ''}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-6">
          {organization.anonymous && (
            <span className="rounded-md bg-white/10 px-2.5 py-1 text-xs font-semibold text-emerald-300 ring-1 ring-emerald-400/40">
              anonymous reporting on
            </span>
          )}
          {viewer && (
            <div className="text-right">
              <p className="text-xs uppercase tracking-widest text-indigo-200">Signed in as</p>
              <p className="font-semibold">{viewer.name || viewer.email}</p>
              <p className="text-sm text-indigo-200">{ROLE[viewer.role] || viewer.role}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Section 1 — adoption
 * ------------------------------------------------------------------ */

const STATUS_DEFS = [
  ['Power user', 'Active on 12+ of the last 30 days'],
  ['Active', 'Something recorded in the last 30 days'],
  ['Dormant', 'Nothing recorded for 30+ days'],
  ['Never started', 'No recorded module, ever'],
];

function AdoptionSection({ rows, allCount, timeSplit, activity, window: win, seats, onOpenLearner }) {
  const by = (s) => rows.filter((m) => m.status === s);
  const power = by('power');
  const never = by('never');
  const dormant = by('dormant');
  const active = by('active');
  const activeInWindow = rows.filter((m) => m.window.activeInWindow);
  const activeLast30 = rows.filter((m) => m.lifetime.activeDaysLast30 > 0);
  const minutes = activeInWindow.map((m) => m.window.totalMs / 60000);

  const weeks = useMemo(
    () =>
      (activity && activity.weeks ? activity.weeks : []).map((w) => ({
        label: fmtDate(new Date(`${w.weekStart}T12:00:00Z`).getTime()).replace(/,? \d{4}$/, ''),
        value: w.activeUsers,
        minutes: w.minutes,
      })),
    [activity]
  );

  const seatPct = seats ? Math.round((100 * activeLast30.length) / seats) : null;

  const statusSlices = [
    { key: 'power', label: 'Power users', definition: '12+ days in last 30', value: power.length, color: '#059669', people: power },
    { key: 'active', label: 'Active', definition: 'active in last 30 days', value: active.length, color: '#6c63ff', people: active },
    { key: 'dormant', label: 'Dormant', definition: 'quiet for 30+ days', value: dormant.length, color: '#f59e0b', people: dormant },
    { key: 'never', label: 'Never started', definition: 'nothing recorded ever', value: never.length, color: '#e5e7eb', people: never },
  ];

  const timeSlices = timeSplit.slices.map((s, i) => ({
    key: s.key,
    label: s.label,
    value: s.ms,
    display: s.ms ? fmtMins(s.ms) : '—',
    color: ['#6c63ff', '#0ea5e9', '#f59e0b'][i],
  }));

  return (
    <Section
      title="Adoption"
      subtitle="Who is showing up, how often, and where the time goes"
      helpWide
      help={<DefinitionTable title="Status bands" rows={STATUS_DEFS} />}
      summary={
        <div className="flex flex-wrap gap-4">
          <CountTile
            label="Power users"
            value={power.length}
            tone="good"
            hint="12+ active days in last 30"
            people={power}
            onOpenLearner={onOpenLearner}
          />
          <CountTile
            label="Active in range"
            value={activeInWindow.length}
            hint={win.sinceStart ? 'since start' : `last ${win.days} days`}
            people={activeInWindow}
            onOpenLearner={onOpenLearner}
          />
          <CountTile
            label="Never started"
            value={never.length}
            tone={never.length ? 'warn' : 'default'}
            hint={`of ${rows.length} in view`}
            people={never}
            onOpenLearner={onOpenLearner}
          />
          <StatTile
            label="Time in range"
            value={(timeSplit.totalMs / 3600000).toFixed(1)}
            unit="h"
            hint={
              timeSplit.untimedAttempts > 0
                ? `${timeSplit.untimedAttempts} of ${timeSplit.attempts} attempts untimed`
                : `${timeSplit.attempts} attempts`
            }
            help={
              <ul className="ml-4 list-disc space-y-1">
                <li>Only attempts with both a start and an end are counted.</li>
                <li>About a quarter never get a closing timestamp — a closed tab or a crash.</li>
                <li>Those add nothing rather than a guess.</li>
              </ul>
            }
          />
          <StatTile
            label="Median time per person"
            value={median(minutes) == null ? '—' : fmtMins(median(minutes) * 60000)}
            hint={`n=${minutes.length} active`}
          />
        </div>
      }
    >
      <div className="grid gap-6 lg:grid-cols-3">
        <Card>
          <h3 className="mb-3 text-lg font-semibold text-navy">
            Seat use
            <HowCalculated>
              <ul className="ml-4 list-disc space-y-1">
                <li>People active in the last 30 days, over seats bought.</li>
                <li>Always 30 days, whatever range is picked above — a target needs a fixed period.</li>
                <li>No seat count on record means no figure, rather than 0%.</li>
              </ul>
            </HowCalculated>
          </h3>
          <TargetBar
            value={seatPct}
            target={ADOPTION_TARGET}
            label="Active on a seat, last 30 days"
            missingLabel="No seat count on record for this organisation."
          />
          {seats && (
            <p className="mt-3 text-xs text-gray-400">
              {activeLast30.length} active of {seats} seats · {allCount} accounts exist
            </p>
          )}
        </Card>

        <Card>
          <h3 className="mb-3 text-lg font-semibold text-navy">
            Engagement
            <HowCalculated wide label="Status bands">
              <DefinitionTable rows={STATUS_DEFS} />
              <span className="block text-gray-500">
                Measured from today, not from the end of the date range — dormancy is a claim about now.
              </span>
            </HowCalculated>
          </h3>
          <Donut
            slices={statusSlices}
            centreValue={rows.length ? `${Math.round((100 * (power.length + active.length)) / rows.length)}%` : '—'}
            centreLabel="active or better"
            onOpenLearner={onOpenLearner}
          />
        </Card>

        <Card>
          <h3 className="mb-3 text-lg font-semibold text-navy">
            Where the time goes
            <HowCalculated>
              Split by recorded duration, not attempt count — a dozen lesson opens can take seconds while one
              negotiation runs twenty minutes.
            </HowCalculated>
          </h3>
          {timeSplit.totalMs > 0 ? (
            <Donut
              slices={timeSlices}
              centreValue={`${(timeSplit.totalMs / 3600000).toFixed(1)}h`}
              centreLabel="in range"
            />
          ) : (
            <Empty>No timed activity in this range.</Empty>
          )}
        </Card>
      </div>

      <Card>
        <h3 className="mb-1 text-lg font-semibold text-navy">
          Weekly active people
          <HowCalculated>
            <ul className="ml-4 list-disc space-y-1">
              <li>One dot per week; a person counts once however much they did.</li>
              <li>Fixed 12 months — the range filter above does not apply.</li>
              <li>Flat dots with rising time means the same people going deeper.</li>
            </ul>
          </HowCalculated>
        </h3>
        {activity ? (
          <DotPlot
            points={weeks}
            of={allCount}
            emptyLabel="Nobody here has recorded activity in the last 12 months"
          />
        ) : (
          <Loading what="activity" />
        )}
      </Card>
    </Section>
  );
}

/* ------------------------------------------------------------------ *
 * Section 2 — improvement
 * ------------------------------------------------------------------ */

function ImprovementSection({ rows, filters, onOpenLearner }) {
  const scored = rows.map((m) => ({ ...m, value: scoreFor(m, filters) })).filter((m) => m.value != null);
  const moved = rows.map((m) => ({ ...m, value: improvementFor(m, filters) })).filter((m) => m.value != null);
  const quizzed = rows
    .map((m) => ({ ...m, value: m.lifetime.avgQuizScore }))
    .filter((m) => m.value != null);

  const improving = moved.filter((m) => m.value > BAND);
  const regressing = moved.filter((m) => m.value < -BAND);

  const scoreStat = statOf(scored.map((m) => m.value));
  const impStat = statOf(moved.map((m) => m.value));
  const quizStat = statOf(quizzed.map((m) => m.value));

  const pts = (v) => (v == null ? '—' : `${v > 0 ? '+' : ''}${Math.round(v)} pts`);

  return (
    <Section
      divider
      title="Improvement"
      subtitle="Case-study scores and whether people are getting better — lifetime, not the selected range"
      helpWide
      help={
        <DefinitionTable
          title="How each figure is built"
          rows={[
            ['Score', "Mean of each person's latest score per case study"],
            ['Improvement', 'Mean of last 3 attempts minus mean of first 3, per case study'],
            ['Improving', `More than +${BAND} pts`],
            ['Regressing', `Worse than −${BAND} pts`],
            ['Excluded', 'Anyone without 2 scored attempts at the same case study'],
          ]}
        />
      }
      summary={
        <div className="flex flex-wrap gap-4">
          <StatTile
            label="Median case study score"
            value={scoreStat.median == null ? '—' : `${Math.round(scoreStat.median)}%`}
            tone="accent"
            hint={
              scoreStat.n ? `mean ${Math.round(scoreStat.mean)}% · n=${scoreStat.n}` : 'nobody scored yet'
            }
            help={
              <ul className="ml-4 list-disc space-y-1">
                <li>Each person contributes one number: their latest score on each case study, averaged.</li>
                <li>Scores are a % of each case study&rsquo;s own maximum.</li>
                <li>Median, not mean — one very strong or weak person moves a small org&rsquo;s average a long way.</li>
              </ul>
            }
          />
          <StatTile
            label="Median improvement"
            value={pts(impStat.median)}
            tone={impStat.median > 0 ? 'good' : 'default'}
            hint={impStat.n ? `mean ${pts(impStat.mean)} · n=${impStat.n}` : 'not measurable yet'}
            help={
              <>
                <b className="block text-navy">&ldquo;Points&rdquo; means percentage points.</b>
                <ul className="ml-4 mt-1 list-disc space-y-1">
                  <li>Every attempt is scored as a % of that case study&rsquo;s own maximum.</li>
                  <li>
                    Improvement = average of the last 3 attempts minus the average of the first 3, per case
                    study.
                  </li>
                  <li>
                    <b>+13 pts</b> means 50% became 63% — not 13% better, and not 13 marks.
                  </li>
                  <li>Percentages, not raw marks, because rubrics differ (4, 6, 14 and 26 all occur).</li>
                </ul>
              </>
            }
          />
          <CountTile
            label="Improving"
            value={improving.length}
            tone="good"
            hint={`more than +${BAND} pts`}
            people={improving}
            onOpenLearner={onOpenLearner}
            unit=" pts"
          />
          <CountTile
            label="Regressing"
            value={regressing.length}
            tone={regressing.length ? 'bad' : 'default'}
            hint={`worse than −${BAND} pts`}
            people={regressing}
            onOpenLearner={onOpenLearner}
            unit=" pts"
          />
          <StatTile
            label="Median quiz score"
            value={quizStat.median == null ? '—' : `${Math.round(quizStat.median)}%`}
            hint={quizStat.n ? `n=${quizStat.n}` : 'no trusted quiz scores yet'}
            help={
              <ul className="ml-4 list-disc space-y-1">
                <li>Averages each person&rsquo;s latest score per quiz.</li>
                <li>
                  Quizzes still recorded as 1 out of 1 are left out — counting them would peg this at 100%.
                </li>
                <li>Fixed by the next Unity build.</li>
              </ul>
            }
          />
        </div>
      }
    >
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <h3 className="mb-4 text-lg font-semibold text-navy">
            Where the org actually sits
            <HowCalculated wide label="What is plotted">
              <DefinitionTable
                rows={[
                  ['X axis', "Average latest case-study score, as a % of each case's own maximum"],
                  ['Bar height', 'How many people fall in that 10-point band'],
                  ['Shaded band', 'The middle 50% of people'],
                  ['Navy rule', 'Median'],
                  ['Dashed red', 'Mean — apart from the median means a tail is dragging it'],
                  ['Click a bar', 'Lists the people in it'],
                ]}
              />
              <span className="block text-gray-500">
                Two clusters mean two problems in one org; one tight clump means a uniform cohort.
              </span>
            </HowCalculated>
          </h3>
          <Histogram
            histogram={histOf(scored.map((m) => m.value))}
            stat={scoreStat}
            series="score"
            members={scored}
            valueOf={(m) => m.value}
            onOpenLearner={onOpenLearner}
          />
        </Card>

        <Card>
          <h3 className="mb-4 text-lg font-semibold text-navy">
            How much people moved
            <HowCalculated wide label="What is plotted">
              <DefinitionTable
                rows={[
                  ['X axis', 'Improvement in percentage points — 0 means no change'],
                  ['Left of zero', 'Scores went down'],
                  ['Bar height', 'How many people fall in that 10-point band'],
                  ['Navy / dashed', 'Median and mean'],
                  ['Click a bar', 'Lists the people in it'],
                ]}
              />
              <span className="block text-gray-500">
                Needs two scored attempts at the same case study, so anyone who never retried is absent
                rather than counted as zero — keen learners are over-represented by construction.
              </span>
            </HowCalculated>
          </h3>
          <Histogram
            histogram={histOf(moved.map((m) => m.value), { lo: -40, hi: 40, width: 10 })}
            stat={impStat}
            unit=" pts"
            series="improvement"
            members={moved}
            valueOf={(m) => m.value}
            onOpenLearner={onOpenLearner}
          />
          <div className="mt-4 border-t border-gray-100 pt-4">
            <TargetBar
              value={impStat.median == null ? null : Math.round(impStat.median)}
              target={10}
              max={40}
              unit=" pts"
              label="Median improvement vs target"
              missingLabel="Nobody has two scored attempts at the same case study yet."
            />
          </div>
          <p className="mt-3 text-xs text-gray-500">
            {rows.length - moved.length} of {rows.length} people cannot be measured yet.
          </p>
        </Card>
      </div>

      {/* Person-vs-person ranking, hidden for now — see metricsFlags.js */}
      {SHOW_LEADERBOARDS && (
        <Leaderboards rows={rows} filters={filters} onOpenLearner={onOpenLearner} />
      )}
    </Section>
  );
}

function Leaderboards({ rows, filters, onOpenLearner }) {
  const top = (valueOf, n = 5) =>
    rows
      .map((m) => ({ ...m, value: valueOf(m) }))
      .filter((m) => m.value != null)
      .sort((a, b) => b.value - a.value)
      .slice(0, n);

  const boards = [
    {
      title: 'Most improved',
      note: 'Rewards movement, not starting position — fairer to people who began at Level 1',
      rows: top((m) => improvementFor(m, filters)),
      render: (v) => <span className="text-sm font-semibold tabular-nums text-emerald-600">▲ {Math.round(v)} pts</span>,
    },
    {
      title: 'Highest current scores',
      note: 'Secondary by design: a raw leaderboard mostly measures who started ahead',
      rows: top((m) => scoreFor(m, filters)),
      render: (v) => (
        <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold tabular-nums text-emerald-700 ring-1 ring-emerald-200">
          {Math.round(v)}%
        </span>
      ),
    },
  ];

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      {boards.map((b) => (
        <Card key={b.title}>
          <h4 className="text-lg font-semibold text-navy">{b.title}</h4>
          <p className="mb-3 text-xs text-gray-500">{b.note}</p>
          {b.rows.length === 0 ? (
            <Empty>Not enough data yet.</Empty>
          ) : (
            <ol className="space-y-2.5">
              {b.rows.map((m, i) => (
                <li key={m.userId} className="flex items-center gap-3 text-sm">
                  <span className="w-4 text-xs tabular-nums text-gray-400">{i + 1}</span>
                  <button
                    onClick={() =>
                      onOpenLearner(m.userId, m.displayLabel, (m.userIds || []).map((u) => u.userId))
                    }
                    className="flex-1 truncate text-left font-medium text-navy hover:text-lavender hover:underline"
                  >
                    {m.displayLabel}
                  </button>
                  {m.level && <span className="text-xs text-gray-400">{m.level.replace(/^Level\s*/i, 'L')}</span>}
                  {b.render(m.value)}
                </li>
              ))}
            </ol>
          )}
        </Card>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Section 3 — content coverage (awaiting its backend)
 * ------------------------------------------------------------------ */

function ContentCoverageSection({ content, onOpenLearner }) {
  if (!content) return null;

  const { chapters, caseStudies, quizzes } = content;
  const reached = chapters.filter((c) => c.startedPct > 0).length;
  const finished = chapters.filter((c) => c.completedPct === 100).length;

  /*
   * These medians are taken over CONTENT, not over people — one value per case
   * study or quiz. Section 2's similarly named tiles are one value per person,
   * and the two answer different questions: "is this material hard" versus "is
   * this cohort doing well". Both `?` popovers say which is which.
   */
  const scored = caseStudies.filter((c) => c.avgPct != null);
  const quizScored = quizzes.filter((q) => q.avgFirstPct != null);
  const caseMedian = median(scored.map((c) => c.avgPct));
  const quizMedian = median(quizScored.map((q) => q.avgFirstPct));

  return (
    <Section
      divider
      title="Content coverage"
      subtitle="Which material the org has reached, and which material is fighting back"
      summary={
        <div className="flex flex-wrap gap-4">
          <StatTile
            label="Chapters reached"
            value={reached}
            unit={`/ ${chapters.length}`}
            hint="at least one person started"
          />
          <StatTile
            label="Chapters finished by all"
            value={finished}
            unit={`/ ${chapters.length}`}
            tone={finished ? 'good' : 'default'}
          />
          <StatTile
            label="Median score per case study"
            value={caseMedian == null ? '—' : `${Math.round(caseMedian)}%`}
            tone="accent"
            hint={
              scored.length
                ? `across ${scored.length} of ${caseStudies.length} case studies`
                : 'nothing scored yet'
            }
            help={
              <ul className="ml-4 list-disc space-y-1">
                <li>One number per CASE STUDY: its average score across everyone who played it.</li>
                <li>The median of those, so one unusually hard case does not set the tone.</li>
                <li>
                  Not the same as Section 2&rsquo;s median, which is one number per <b>person</b>.
                </li>
              </ul>
            }
          />
          <StatTile
            label="Median score per quiz"
            value={quizMedian == null ? '—' : `${Math.round(quizMedian)}%`}
            hint={
              quizScored.length
                ? `first attempts, across ${quizScored.length} of ${quizzes.length} quizzes`
                : 'no trusted quiz scores yet'
            }
            help={
              <ul className="ml-4 list-disc space-y-1">
                <li>One number per QUIZ: the average first attempt across everyone who took it.</li>
                <li>First attempts only — a retry measures persistence, not whether the lesson landed.</li>
                <li>Quizzes still recorded as 1 out of 1 are excluded, so this reads &ldquo;—&rdquo; until the next Unity build.</li>
              </ul>
            }
          />
        </div>
      }
    >
      <ContentCoverage content={content} onOpenLearner={onOpenLearner} />
    </Section>
  );
}

/* ------------------------------------------------------------------ *
 * Section 4 — people
 * ------------------------------------------------------------------ */

const FLAG_DEFS = [
  ['Never started', 'No recorded module, ever'],
  ['Gone quiet', 'Nothing recorded for 30+ days'],
  ['Scoring low', 'Average latest case-study score under 55%'],
  ['Barely progressed', 'Started, but under 15% of their path complete'],
  ['Scores falling', `Improvement worse than −${BAND} points`],
];

const REASON_LABEL = {
  neverStarted: 'never started',
  lowScore: 'scoring low',
  lowProgress: 'barely progressed',
  declining: 'scores falling',
};
const reasonText = (r) => REASON_LABEL[r] || (r.startsWith('noActivity') ? 'gone quiet' : r);

function PeopleSection({ rows, attention, anonymous, orgName, onOpenLearner }) {
  // The attention list comes from the server over the whole roster; narrow it to
  // whoever is in view so it cannot contradict the table beneath it.
  const visible = new Set(rows.map((m) => String(m.userId)));
  const shown = attention.filter((a) => visible.has(String(a.userId)));
  const byUserId = new Map(rows.map((m) => [String(m.userId), (m.userIds || []).map((u) => u.userId)]));

  return (
    <Section
      divider
      title="People"
      subtitle={
        anonymous
          ? 'One row per employee, anonymised — click any row to open their full progress page'
          : 'One row per employee — click any row to open their full progress page'
      }
      summary={
        shown.length > 0 ? (
          /*
           * A tile, not the whole list.
           *
           * Rendered inline this was 125 chips — several screens of names above
           * the table they summarise, which buried the table and made the page
           * read as a list of problems rather than a roster. As a tile it
           * states the count, and the names are one click away: five to begin
           * with, the rest behind "+N more", which is how every other count on
           * these pages behaves.
           */
          <div className="flex flex-wrap gap-3">
            <CountTile
              label="Worth a conversation"
              value={shown.length}
              tone={shown.length ? 'warn' : 'default'}
              hint={`${shown.length === 1 ? 'person matches' : 'people match'} at least one flag`}
              items={shown.map((a) => ({
                id: a.userId,
                label: a.displayLabel,
                note: a.reasons.map(reasonText).join(', '),
                /* The attention list is computed server-side over the roster and
                   does not carry per-region ids, so they are taken from the row
                   it refers to — otherwise opening somebody from here sends one
                   region's id and can 404. */
                userIds: byUserId.get(String(a.userId)) || [],
              }))}
              onOpen={onOpenLearner}
              max={5}
              width={520}
              help={
                <>
                  <DefinitionTable rows={FLAG_DEFS} />
                  <span className="block text-gray-500">
                    Ordered by how many flags apply. Click a name to open their progress.
                  </span>
                </>
              }
            />
          </div>
        ) : (
          <Card>
            <p className="text-sm text-gray-600">Nobody in view matches an attention flag.</p>
          </Card>
        )
      }
    >
      <PeopleTable members={rows} anonymous={anonymous} orgName={orgName} onOpenLearner={onOpenLearner} />
    </Section>
  );
}
